import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { VideoFeatures, useVideoFeatures } from '@/store/useVideoFeatures';
import { defaultSalesNotification } from '@/store/videoFeatures/types/sales-notification';
import { defaultFinalCountdown } from '@/store/videoFeatures/types/final-countdown';
import { Video } from '@/store/videoDashboard';
import { t } from '@/utils/i18n';
import { toPercentOrNaN } from '@/utils/autoplayOverlayHydration';
import { actionButtonsFromVideo } from '@/lib/action-button/api-mapping';

export function useVideoEditor() {
  useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Usar o store com selector para garantir reatividade
  const features = useVideoFeatures((state) => state.features);
  const setFeature = useVideoFeatures((state) => state.setFeature);
  const saveFeatures = useVideoFeatures((state) => state.saveFeatures);
  const hasUnsavedChanges = useVideoFeatures((state) => state.hasUnsavedChanges);
  const setHasUnsavedChanges = useVideoFeatures((state) => state.setHasUnsavedChanges);
  const setCurrentFeature = useVideoFeatures((state) => state.setCurrentFeature);
  const currentFeature = useVideoFeatures((state) => state.currentFeature);

  const { toast } = useToast();
  const { user } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [modalSubtitle, setModalSubtitle] = useState('');
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  // Get active panel from URL or state
  const tabFromUrl = searchParams.get('tab');
  const [activeEditingPanel, setActiveEditingPanelState] = useState<string | null>(tabFromUrl);

  // Handle save - salva todas as configurações no backend
  const handleSave = async () => {
    try {
      const videoId = features.videoId;

      if (!videoId) {
        toast({
          title: translateInterfaceText("Erro ao salvar"),
          description: translateInterfaceText("Nenhum vídeo selecionado para salvar"),
          variant: "destructive"
        });
        return;
      }

      // Import videoService
      const { videoService } = await import('@/services/videoService');

      // Mapeamento de posições frontend -> backend
      const positionToBackend: Record<string, string> = {
        'top-left': 'S_Esquerda',
        'top-center': 'S_Meio',
        'top-right': 'S_Direita',
        'center-left': 'M_Esquerda',
        'center': 'M_Meio',
        'center-right': 'M_Direita',
        'bottom-left': 'I_Esquerda',
        'bottom-center': 'I_Meio',
        'bottom-right': 'I_Direita',
      };

      // Normaliza o tamanho da headline para número (px). Aceita number direto
      // do state novo, string numérica legada ('16','24','32') e enum antigo
      // ('small'/'medium'/'large'). Após a migration no banco, todos os novos
      // salvamentos vão como INT.
      const LEGACY_SIZE_MAP: Record<string, number> = {
        small: 16, medium: 24, large: 32, xlarge: 40,
        pequeno: 16, medio: 24, grande: 32,
      };
      const resolveSizePx = (raw: any, defaultPx: number): number => {
        if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
        if (typeof raw === 'string') {
          const num = parseInt(raw, 10);
          if (!isNaN(num) && num > 0) return num;
          const legacy = LEGACY_SIZE_MAP[raw.trim().toLowerCase()];
          if (legacy) return legacy;
        }
        return defaultPx;
      };

      // Construir objeto com TODOS os campos para o backend
      const dataToSave: Record<string, any> = {
        // === AUTOPLAY ===
        haveAutoplay: features.autoplayOverlay?.enabled || features.autoPlay || false,
        cor: features.autoplayOverlay?.colors?.background || 'rgba(0, 170, 255, 0.46)',
        corText: features.autoplayOverlay?.colors?.text || '#FFFFFF',
        textSuperior: features.autoplayOverlay?.textTop || '',
        textInferior: features.autoplayOverlay?.textBottom || '',
        typesAutoplay: features.autoplayOverlay?.templateId || 'autoPlay',
        autoplayIconColor: features.autoplayOverlay?.colors?.icon || '#FFFFFF',
        haveSmallTemplate: features.autoplayOverlay?.templateId === 'small',
        // Salva só o primário. O legado (`pulseEffect`/`showBorder`) é duplicado
        // no load do backend, mas só o primário é atualizado pelos switches —
        // se combinássemos com OR aqui, o toggle do usuário seria perdido (o
        // legado travaria o valor em `true` para sempre).
        havePulse: features.autoplayOverlay?.pulseAnimation || false,
        haveRestart: features.haveRestart ?? false,
        haveBorderWhite: features.autoplayOverlay?.whiteBorder || false,
        haveCustomImage: features.autoplayOverlay?.customImage ? true : false,
        customImage: features.autoplayOverlay?.customImage || null,
        // Posição/tamanho custom do overlay de autoplay (templates
        // autoPlay/small/large/transparent). Quando o usuário arrasta ou
        // redimensiona o overlay no editor, esses valores são persistidos
        // em % do container do vídeo e lidos pelo runner.js do playerjs
        // para aplicar o mesmo layout em produção.
        autoplayPositionXPercent: features.autoplayOverlay?.position?.xPercent ?? null,
        autoplayPositionYPercent: features.autoplayOverlay?.position?.yPercent ?? null,
        autoplayWidthPercent: features.autoplayOverlay?.size?.widthPercent ?? null,
        autoplayHeightPercent: features.autoplayOverlay?.size?.heightPercent ?? null,
        autoplayDetectInteraction: features.autoplayOverlay?.detectInteraction === true,
        autoplayAutoClick: features.autoplayOverlay?.autoClick === true,
        // Tela flutuante (PiP) do autoplay — coluna dedicada, NÃO combinar com
        // o PiP dos controles (`havePictureInPicture`) para não vazar o botão
        // de um recurso para o outro.
        autoplayPictureInPicture: features.autoplayOverlay?.havePictureInPicture === true,

        // === SMART PAUSE ===
        haveSmartPause: features.smartPause ?? false,

        // === BLOQUEIO DE PAUSE ===
        haveBlockPause: features.blockPause ?? false,

        // === BORDER ===
        haveBorder: features.border?.enabled || false,
        borderColor: features.border?.color || '#ffffff',
        borderWidth: features.border?.width || 2,
        borderRadius: features.border?.radius ?? 0,

        // === CONTROLS ===
        // Se controles não estão ativos, reset completo para não enviar dados sujos ao backend
        haveControls: features.showControls || false,
        haveBigPlay: features.showControls ? (features.bigPlayButton || false) : false,
        haveProgresso: features.showControls ? (features.showProgressBar || false) : false,
        haveSkip: features.showControls ? (features.skipForward || false) : false,
        haveRewind: features.showControls ? (features.rewind || false) : false,
        haveVolume: features.showControls ? (features.volumeControl || false) : false,
        // O mesmo campo `haveFullScreen` é compartilhado por dois recursos
        // (fullscreen nos controles e fullscreen no autoplay). Enviamos true
        // se qualquer um estiver ativo, pra não sobrescrever o outro.
        haveFullScreen: !!(
          (features.showControls && features.fullScreen) ||
          features.autoplayOverlay?.haveFullScreen
        ),
        havePlaybackSpeed: features.showControls ? (features.playbackSpeedControl || false) : false,
        // TV Cast depende dos controles reais — o botão vive na barra de controles.
        haveTvCast: features.showControls ? (features.tvCastEnabled || false) : false,
        // Storyboard depende dos controles reais (não funciona com fake bar — barra fake
        // é mutuamente exclusiva com showControls).
        haveStoryboard: features.showControls ? (features.storyboardEnabled || false) : false,
        haveTempoAtual: features.showControls ? (features.showCurrentTime || false) : false,
        haveSmallPlay: features.showControls ? (features.smallPlayButton || false) : false,
        controlColor: features.showControls ? (features.controlsColor || '#0129BB') : '#0129BB',

        // === PROGRESS BAR ===
        corBarra: features.progressBarColor || features.progressBar?.color || '#3B82F6',
        haveFakeBar: features.progressBar?.enabled || false,
        fakeBarHeight: features.progressBar?.height || features.progressBarHeight || 4,

        // === AUDIENCE (AO VIVO) ===
        haveAoVivo: features.audience?.enabled || false,
        backgroundAoVivo: features.audience?.style?.backgroundColor || '#000000',
        textAoVivo: features.audience?.message || '',
        simulationAoVivo: features.audience?.viewersCount != null ? String(features.audience.viewersCount) : '',
        audienceSimulationSpeed: features.audience?.speed || 'medium',
        fontColorAoVivo: features.audience?.style?.textColor || '#ffffff',

        // === CONTINUE WATCHING ===
        haveContinue: features.continueWatching?.enabled ?? false,
        textContinue: features.continueWatchingOverlay?.continueText || 'Continuar assistindo?',
        textMessage: features.continueWatchingOverlay?.message || 'Você já começou a assistir esse vídeo',
        textRestart: features.continueWatchingOverlay?.restartText || 'Assistir do início',
        backgroundContinue: features.continueWatchingOverlay?.backgroundColor || '#0E0E0E',
        continueTextColor: features.continueWatchingOverlay?.textColor || '#FFFFFF',
        continuePlayButtonBgColor: features.continueWatchingOverlay?.playButtonBgColor || '',
        continueRestartButtonBgColor: features.continueWatchingOverlay?.restartButtonBgColor || '',
        typesContinue: features.continueWatchingOverlay?.template === 'continue' ? '' : (features.continueWatchingOverlay?.template || 'template1'),

        // === HEADLINE ===
        haveHeadline: features.headline?.enabled || false,
        headlineText: features.headline?.headline?.text || '',
        subHeadlineText: features.headline?.subheadline?.text || '',
        headlineColor: features.headline?.headline?.color || '#ffffff',
        subHeadlineColor: features.headline?.subheadline?.color || '#e5e5e5',
        headlineSize: resolveSizePx(features.headline?.headline?.size, 24),
        subHeadlineSize: resolveSizePx(features.headline?.subheadline?.size, 16),
        headlineTypeFont: features.headline?.headline?.fontFamily || 'Inter',
        headlineFontWeight: features.headline?.headline?.fontWeight || 'normal',
        isHTMLSelected: features.headline?.htmlMode?.enabled || false,
        headlineHtmlCode: features.headline?.htmlMode?.code || '',
        headlineInitialDelay: features.headline?.timing?.start || '',
        headlineEndDelay: features.headline?.timing?.end || '',
        headlineShowThroughout: features.headline?.timing?.showThroughout ?? true,

        // === THUMBNAIL BUTTON (na pausa) ===
        haveButtonThumb: features.pauseThumbnail?.actionButton ? true : false,
        selectedSize: features.pauseThumbnail?.actionButton?.size || 'medium',
        textButton: features.pauseThumbnail?.actionButton?.text || '',
        textLink: features.pauseThumbnail?.actionButton?.link || '',
        backgroundButton: features.pauseThumbnail?.actionButton?.colors?.background || '#3b82f6',
        backgroundButtonHover: features.pauseThumbnail?.actionButton?.colors?.hover || '#2563eb',
        textButtonColor: features.pauseThumbnail?.actionButton?.colors?.text || '#ffffff',
        textButtonColorHover: features.pauseThumbnail?.actionButton?.colors?.hoverText || '#ffffff',
        selectedPosition: positionToBackend[features.pauseThumbnail?.actionButton?.position || 'bottom-center'] || 'I_Meio',
        havePause: features.pauseThumbnail?.enabled || false,
        thumbPauseMode: features.pauseThumbnail?.pauseMode || 'always',
        thumbPauseTimeStart: features.pauseThumbnail?.timeRange?.start || '',

        // === MISC ===
        noneControlsMoreAutoplay: features.disableControls || false,
      };

      // Upload da imagem customizada do autoplay (binário → Bunny CDN).
      // Tem que rodar ANTES do saveAllConfigurations, senão a blob URL local
      // sobrescreveria a URL real do Bunny.
      const { useVideoFeaturesStore } = await import('@/store/videoFeatures/store');
      const autoplayFile = useVideoFeaturesStore.getState().tempFiles.autoplayCustomImageFile;
      if (autoplayFile) {
        const formData = new FormData();
        formData.append('customImage', autoplayFile);
        const uploadResp = await videoService.updateAutoplayCustomImage(videoId, formData);
        const finalUrl = uploadResp?.customImage;
        if (finalUrl) {
          dataToSave.customImage = finalUrl;
          dataToSave.haveCustomImage = true;
          setFeature('autoplayOverlay', {
            ...features.autoplayOverlay,
            customImage: finalUrl,
          });
        }
        useVideoFeaturesStore.getState().clearTempFile('autoplayCustomImageFile');
      } else if (typeof dataToSave.customImage === 'string' && dataToSave.customImage.startsWith('blob:')) {
        dataToSave.customImage = null;
      }

      // Chamar a API para salvar todas as configurações
      await videoService.saveAllConfigurations(videoId, dataToSave);

      // ============================================
      // LÓGICA DE VELOCIDADE (MANUAL E AUTOMÁTICO)
      // ============================================

      // Verifica se teste automático está pendente
      const pendingAutomaticTest = features.speedTests?.find(
        test => test.enabled && test.isAutomatic && test.testStatus === 'pending'
      );

      const isAutomaticTestPending = !!pendingAutomaticTest;

      // Verifica se velocidade manual está ativa
      const isManualSpeedActive = features.playbackSpeed !== undefined && features.playbackSpeed !== 1.0;

      // Cenário 1: Velocidade Manual Ativa (com ou sem teste automático)
      if (isManualSpeedActive) {
        try {
          await videoService.updateSpeedTest(videoId, {
            haveTurbo: true,
            haveTurboTest: false,
            turboVelocity: features.playbackSpeed,
            turboVelocityRangeBegin: 1,
            turboVelocityRangeEnd: features.playbackSpeed,
          });

          toast({
            title: translateInterfaceText("Velocidade manual salva"),
            description: translateInterfaceText("Velocidade configurada para {value0}x", { value0: (features.playbackSpeed) }),
          });
        } catch (error: any) {
          console.error('Error saving manual speed:', error);
          toast({
            title: translateInterfaceText("Erro ao salvar velocidade manual"),
            description: error?.message || translateInterfaceText("Não foi possível salvar a velocidade"),
            variant: "destructive"
          });
        }
      }

      // Cenário 2: Teste Automático Pendente (com ou sem velocidade manual)
      if (isAutomaticTestPending && pendingAutomaticTest) {
        try {
          // Import speedTestService
          const { speedTestService } = await import('@/services/speedTestService');

          // Obter userId do contexto de auth (fonte de verdade; não do storage)
          const userId = user?.id || user?.id_user;

          if (!userId) {
            throw new Error(translateInterfaceText("Usuário não autenticado"));
          }

          // Gerar array de velocidades entre velocidadeInicial e velocidadeFinal
          const start = pendingAutomaticTest.velocidadeInicial || 1.0;
          const end = pendingAutomaticTest.velocidadeFinal || 1.5;
          const speeds: number[] = [];

          // Calcular número de steps (incrementos de 0.1)
          const steps = Math.round((end - start) / 0.1) + 1;

          for (let i = 0; i < steps; i++) {
            const speed = parseFloat((start + i * 0.1).toFixed(1));
            speeds.push(speed);
          }

          // Criar teste no backend via POST
          const createdTest = await speedTestService.createSpeedTest(
            videoId,
            userId,
            speeds,
            pendingAutomaticTest.endDate || undefined,
          );

          // Atualizar status do teste para 'running' no store e salvar o ID real do backend
          const updatedTests = features.speedTests?.map(test =>
            test.id === pendingAutomaticTest.id
              ? { ...test, id: createdTest.id_speed_test, testStatus: 'running' as const }
              : test
          ) || [];

          setFeature('speedTests', updatedTests);

          toast({
            title: translateInterfaceText("Teste automático iniciado"),
            description: translateInterfaceText("O teste de velocidade foi criado e está coletando dados"),
          });
        } catch (error: any) {
          console.error('Error creating automatic speed test:', error);
          toast({
            title: translateInterfaceText("Erro ao criar teste automático"),
            description: error?.message || translateInterfaceText("Não foi possível criar o teste de velocidade"),
            variant: "destructive"
          });
        }
      }

      // Cenário 3: Nenhum recurso ativo - Desativar velocidade
      if (!isManualSpeedActive && !isAutomaticTestPending) {
        const hasAnySpeedTest = features.speedTests?.some(test => test.enabled);

        // Se não tem velocidade manual nem teste ativo/pendente, desativa no backend
        if (!hasAnySpeedTest) {
          try {
            await videoService.updateSpeedTest(videoId, {
              haveTurbo: false,
              haveTurboTest: false,
              turboVelocity: 1.0,
              turboVelocityRangeBegin: 1,
              turboVelocityRangeEnd: 1,
            });
          } catch (error: any) {
            console.error('Error disabling speed:', error);
            // Não precisa mostrar erro se for apenas desativação
          }
        }
      }

      // Marcar como salvo no store
      await saveFeatures();

      toast({
        title: translateInterfaceText("Alterações salvas com sucesso"),
        description: translateInterfaceText("Todas as configurações do seu vídeo foram salvas")
      });
      setIsSaved(true);
    } catch (error: any) {
      console.error('Error saving video configurations:', error);
      toast({
        title: translateInterfaceText("Falha ao salvar alterações"),
        description: error?.response?.data?.message || error?.message || translateInterfaceText("Ocorreu um erro ao salvar suas alterações"),
        variant: "destructive"
      });
    }
  };

  // Handle cancel with confirmation
  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setPendingNavigation('/');
      setShowUnsavedDialog(true);
    } else {
      navigate('/');
    }
  };

  // Navigation with unsaved changes protection
  const handleNavigate = useCallback((path: string) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(path);
      setShowUnsavedDialog(true);
    } else {
      navigate(path);
    }
  }, [hasUnsavedChanges, navigate]);

  const confirmNavigation = useCallback(() => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setShowUnsavedDialog(false);
      setPendingNavigation(null);
      setHasUnsavedChanges(false);
    }
  }, [pendingNavigation, navigate, setHasUnsavedChanges]);

  // Update URL with query parameter
  const setActiveEditingPanel = useCallback((featureId: string | null) => {
    setActiveEditingPanelState(featureId);

    if (featureId) {
      // Add query parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', featureId);
      setSearchParams(newSearchParams, { replace: true });
    } else {
      // Remove query parameter
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('tab');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Handle feature click for side-by-side editing
  const handleFeatureClick = useCallback((featureId: string) => {
    setActiveEditingPanel(featureId);
    setCurrentFeature(featureId);

    // Set modal title and subtitle based on feature ID
    switch (featureId) {
      case 'controls':
        setModalTitle('Controles do Player');
        setModalSubtitle('');
        break;
      case 'progressBar':
        setModalTitle('Barra Fake');
        setModalSubtitle('');
        break;
      case 'loop':
        setModalTitle(t('loopVideo'));
        setModalSubtitle('');
        break;
      case 'headline':
        setModalTitle(t('headline'));
        setModalSubtitle('');
        break;
      case 'thumbnail':
        setModalTitle(t('thumbnail'));
        setModalSubtitle('');
        break;
      case 'actionButton':
        setModalTitle(t('actionButton'));
        setModalSubtitle('');
        break;
      case 'playbackSpeed':
        setModalTitle('Velocidade de Reprodução');
        setModalSubtitle('');
        break;
      case 'audience':
        setModalTitle(t('audienceSimulation'));
        setModalSubtitle('');
        break;
      case 'continueWatching':
        setModalTitle(t('continueWatching'));
        setModalSubtitle('');
        break;
      case 'border':
        setModalTitle(t('customizeBorder'));
        setModalSubtitle('');
        break;
      case 'logo':
        setModalTitle(t('logo'));
        setModalSubtitle('');
        break;
      case 'pixel':
        setModalTitle(t('pixel'));
        setModalSubtitle('');
        break;
      case 'smartPause':
        setModalTitle('Pausa Inteligente');
        setModalSubtitle('');
        break;
      case 'haveRestart':
        setModalTitle('Recomeçar após o fim');
        setModalSubtitle('');
        break;
      case 'subtitles':
        setModalTitle('Legendas');
        setModalSubtitle('');
        break;
      // OCULTO TEMPORARIAMENTE - Será implementado futuramente
      // case 'affiliate':
      //   setModalTitle(t('affiliates'));
      //   setModalSubtitle('');
      //   break;
      case 'autoplayOverlay':
        setModalTitle('Autoplay');
        setModalSubtitle('');
        break;
      case 'salesNotification':
        setModalTitle('Notificação de Vendas');
        setModalSubtitle('');
        break;
      // OCULTO TEMPORARIAMENTE - Será implementado futuramente
      // case 'syncActions':
      //   setModalTitle('Ações Sincronizadas');
      //   setModalSubtitle('');
      //   break;
      case 'finalCountdown':
        setModalTitle('Contagem Final Inteligente');
        setModalSubtitle('');
        break;
      case 'responsivePlayer':
        setModalTitle('Exibição por Dispositivo');
        setModalSubtitle('');
        break;
      case 'miniHooks':
        setModalTitle('Mini Hook');
        setModalSubtitle('');
        break;
      case 'conversionJourney':
        setModalTitle('Jornada de Conversão');
        setModalSubtitle('');
        break;
      case 'videoFeedback':
        setModalTitle('Feedback do Vídeo');
        setModalSubtitle('');
        break;
      default:
        setModalTitle(t('configureFeature'));
        setModalSubtitle('');
    }
  }, [setActiveEditingPanel]);

  // Handle closing the editor
  const handleCloseEditor = useCallback(() => {
    setActiveEditingPanel(null);
    setCurrentFeature(null);
  }, [setActiveEditingPanel, setCurrentFeature]);

  // Initialize from URL on mount and handle URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeEditingPanel) {
      setActiveEditingPanelState(tabFromUrl);
      // Set modal title and subtitle when initializing from URL
      switch (tabFromUrl) {
        case 'controls':
          setModalTitle('Controles do Player');
          setModalSubtitle('');
          break;
        case 'progressBar':
          setModalTitle('Barra Fake');
          setModalSubtitle('');
          break;
        case 'loop':
          setModalTitle(t('loopVideo'));
          setModalSubtitle('');
          break;
        case 'headline':
          setModalTitle(t('headline'));
          setModalSubtitle('');
          break;
        case 'thumbnail':
          setModalTitle(t('thumbnail'));
          setModalSubtitle('');
          break;
        case 'actionButton':
          setModalTitle(t('actionButton'));
          setModalSubtitle('');
          break;
        case 'playbackSpeed':
          setModalTitle('Velocidade de Reprodução');
          setModalSubtitle('');
          break;
        case 'audience':
          setModalTitle(t('audienceSimulation'));
          setModalSubtitle('');
          break;
        case 'continueWatching':
          setModalTitle(t('continueWatching'));
          setModalSubtitle('');
          break;
        case 'border':
          setModalTitle(t('customizeBorder'));
          setModalSubtitle('');
          break;
        case 'logo':
          setModalTitle(t('logo'));
          setModalSubtitle('');
          break;
        case 'pixel':
          setModalTitle(t('pixel'));
          setModalSubtitle('');
          break;
        case 'smartPause':
          setModalTitle('Pausa Inteligente');
          setModalSubtitle('');
          break;
        case 'subtitles':
          setModalTitle('Legendas');
          setModalSubtitle('');
          break;
        // OCULTO TEMPORARIAMENTE - Será implementado futuramente
        // case 'affiliate':
        //   setModalTitle(t('affiliates'));
        //   setModalSubtitle('');
        //   break;
        case 'autoplayOverlay':
          setModalTitle('Autoplay');
          setModalSubtitle('');
          break;
        case 'salesNotification':
          setModalTitle('Notificação de Vendas');
          setModalSubtitle('');
          break;
        // OCULTO TEMPORARIAMENTE - Será implementado futuramente
        // case 'syncActions':
        //   setModalTitle('Ações Sincronizadas');
        //   setModalSubtitle('');
        //   break;
        case 'finalCountdown':
          setModalTitle('Contagem Final Inteligente');
          setModalSubtitle('');
          break;
        case 'responsivePlayer':
          setModalTitle('Exibição por Dispositivo');
          setModalSubtitle('');
          break;
        case 'videoFeedback':
          setModalTitle('Feedback do Vídeo');
          setModalSubtitle('');
          break;
        default:
          setModalTitle(t('configureFeature'));
          setModalSubtitle('');
      }
    }
  }, [location.pathname, searchParams]);

  // Effect to ensure mutual exclusivity between activeEditingPanel and currentFeature
  useEffect(() => {
    if (activeEditingPanel && currentFeature && activeEditingPanel !== currentFeature) {
      setCurrentFeature(null);
    }
  }, [activeEditingPanel, currentFeature, setCurrentFeature]);

  // Handle applying changes
  const handleApplyChanges = useCallback(async () => {
    try {
      // Get current tab from URL query params
      const urlParams = new URLSearchParams(window.location.search);
      const activeTab = urlParams.get('tab');
      const videoId = features.videoId;

      if (!videoId) {
        console.warn('No video ID found, cannot apply changes');
        return;
      }

      // Import services and store
      const { videoService } = await import('@/services/videoService');
      const { useVideoFeaturesStore } = await import('@/store/videoFeatures/store');
      const { toast } = await import('@/hooks/use-toast');

      const tempFiles = useVideoFeaturesStore.getState().tempFiles;
      const clearAllTempFiles = useVideoFeaturesStore.getState().clearAllTempFiles;

      let keepEditorOpen = false;

      // Detect active resource and call appropriate API
      switch (activeTab) {
        case 'playbackSpeed':
          // Turbo/Velocity - Manual or Automatic test
          const pendingTest = features.speedTests?.find(
            test => test.enabled && test.isAutomatic && test.testStatus === 'pending'
          );

          const isManualActive = features.playbackSpeed !== undefined && features.playbackSpeed !== 1.0;

          // Cenário 1: Velocidade Manual Ativa
          if (isManualActive) {
            await videoService.updateSpeedTest(videoId, {
              haveTurbo: true,
              haveTurboTest: false,
              turboVelocity: features.playbackSpeed,
              turboVelocityRangeBegin: 1,
              turboVelocityRangeEnd: features.playbackSpeed,
            });
          }

          // Cenário 2: Teste Automático Pendente
          if (pendingTest) {
            const { speedTestService } = await import('@/services/speedTestService');
            // Obter userId do contexto de auth (fonte de verdade; não do storage)
            const userId = user?.id || user?.id_user;

            if (!userId) {
              throw new Error(translateInterfaceText("Usuário não autenticado"));
            }

            // Gerar array de velocidades
            const start = pendingTest.velocidadeInicial || 1.0;
            const end = pendingTest.velocidadeFinal || 1.5;
            const speeds: number[] = [];
            const steps = Math.round((end - start) / 0.1) + 1;

            for (let i = 0; i < steps; i++) {
              const speed = parseFloat((start + i * 0.1).toFixed(1));
              speeds.push(speed);
            }

            // Criar teste no backend via POST
            const createdTest = await speedTestService.createSpeedTest(
              videoId,
              userId,
              speeds,
              pendingTest.endDate || undefined,
            );

            // Atualizar status do teste para 'running' e salvar o ID real do backend
            const updatedTests = features.speedTests?.map(test =>
              test.id === pendingTest.id
                ? { ...test, id: createdTest.id_speed_test, testStatus: 'running' as const }
                : test
            ) || [];

            setFeature('speedTests', updatedTests);

            // Manter o editor aberto para o usuário copiar o código
            keepEditorOpen = true;
          }

          // Cenário 3: Nenhum ativo - Desativar
          if (!isManualActive && !pendingTest) {
            await videoService.updateSpeedTest(videoId, {
              haveTurbo: false,
              haveTurboTest: false,
              turboVelocity: 1.0,
              turboVelocityRangeBegin: 1,
              turboVelocityRangeEnd: 1,
            });
          }

          if (pendingTest) {
            // Teste recém-criado: o usuário precisa copiar o código pra ativá-lo no site
            toast({
              title: translateInterfaceText("Teste de velocidade iniciado"),
              description: translateInterfaceText("Copie o código do teste e atualize o embed no seu site para que ele comece a rodar."),
            });
          } else {
            toast({
              title: translateInterfaceText("Velocidade atualizada"),
              description: translateInterfaceText("As configurações de velocidade foram salvas com sucesso"),
            });
          }
          break;

        case 'logo': {
          const logoPositionMap: Record<string, string> = {
            'top-left': 'S_Esquerda',
            'top-center': 'S_Meio',
            'top-right': 'S_Direita',
            'bottom-left': 'I_Esquerda',
            'bottom-center': 'I_Meio',
            'bottom-right': 'I_Direita',
          };

          if (tempFiles.logoFile) {
            const formData = new FormData();
            formData.append('logoImg', tempFiles.logoFile);
            if (features.logo?.position) formData.append('selectedLogoPosition', logoPositionMap[features.logo.position] || features.logo.position);
            if (features.logo?.linkUrl) formData.append('urlLogo', features.logo.linkUrl);
            if (features.logo?.opacity !== undefined) formData.append('transparencyLogo', String(features.logo.opacity));
            if (features.logo?.size !== undefined) formData.append('logoSize', String(features.logo.size));

            await videoService.updateLogo(videoId, formData);
            clearAllTempFiles();
            toast({
              title: translateInterfaceText("Logo atualizada"),
              description: translateInterfaceText("A logo foi enviada e salva com sucesso"),
            });
          } else if (features.logo) {
            const formData = new FormData();
            if (features.logo.position) formData.append('selectedLogoPosition', logoPositionMap[features.logo.position] || features.logo.position);
            if (features.logo.linkUrl) formData.append('urlLogo', features.logo.linkUrl);
            if (features.logo.opacity !== undefined) formData.append('transparencyLogo', String(features.logo.opacity));
            if (features.logo.size !== undefined) formData.append('logoSize', String(features.logo.size));

            await videoService.updateLogo(videoId, formData);
            toast({
              title: translateInterfaceText("Configurações da logo atualizadas"),
              description: translateInterfaceText("As configurações da logo foram salvas com sucesso"),
            });
          }
          break;
        }

        case 'thumbnail':
        case 'pauseThumbnail': {
          // As sub-abas (início, pausa, final) podem ter arquivos pendentes ao
          // mesmo tempo quando o usuário troca de aba sem clicar em Aplicar.
          // Enviamos TODAS as que tiverem arquivo novo, batendo em cada rota.
          const initialFile = tempFiles.initialThumbnailFile;
          const pauseFile = tempFiles.pauseThumbnailFile;
          const endFile = tempFiles.endThumbnailFile;

          // Monta o FormData da pausa (modo, tempo e botão de ação). Reutilizado
          // tanto no envio da imagem quanto ao salvar apenas o botão.
          const buildPauseFormData = (file?: File) => {
            const formData = new FormData();
            if (file) formData.append('thumb', file);

            formData.append('havePause', String(features.pauseThumbnail?.enabled ?? false));

            if (features.pauseThumbnail?.pauseMode)
              formData.append('thumbPauseMode', features.pauseThumbnail.pauseMode);
            if (features.pauseThumbnail?.timeRange?.start !== undefined)
              formData.append('thumbPauseTimeStart', features.pauseThumbnail.timeRange.start);

            if (features.pauseThumbnail?.actionButton) {
              const button = features.pauseThumbnail.actionButton;
              const positionMap: Record<string, string> = {
                'top-left': 'S_Esquerda',
                'top-center': 'S_Meio',
                'top-right': 'S_Direita',
                'center-left': 'M_Esquerda',
                'center-right': 'M_Direita',
                'bottom-left': 'I_Esquerda',
                'bottom-center': 'I_Meio',
                'bottom-right': 'I_Direita',
              };
              const selectedPosition = button.position ? positionMap[button.position] || 'I_Meio' : 'I_Meio';

              formData.append('haveButtonThumb', 'true');
              formData.append('selectedSize', button.size || 'medio');
              formData.append('textButton', button.text || '');
              formData.append('textLink', button.link || '');
              formData.append('backgroundButton', button.colors?.background || '#3b82f6');
              formData.append('backgroundButtonHover', button.colors?.hover || '#2563eb');
              formData.append('textButtonColor', button.colors?.text || '#ffffff');
              formData.append('textButtonColorHover', button.colors?.hoverText || '#ffffff');
              formData.append('selectedPosition', selectedPosition);
            } else {
              formData.append('haveButtonThumb', 'false');
            }

            return formData;
          };

          const uploadCount = [initialFile, pauseFile, endFile].filter(Boolean).length;

          // Início
          if (initialFile) {
            const formData = new FormData();
            formData.append('thumbInicio', initialFile);
            await videoService.updateThumbnailStart(videoId, formData);
          }

          // Pausa — sempre enviada, com ou sem imagem nova. Modo, faixa de tempo
          // e o botão (inclusive desligado, via `haveButtonThumb: false`) vivem
          // neste request; enviá-lo só quando havia arquivo ou botão ativo fazia
          // essas edições sumirem sem aviso.
          if (features.pauseThumbnail) {
            await videoService.updateThumbnailPause(videoId, buildPauseFormData(pauseFile));
          }

          // Final
          if (endFile) {
            const formData = new FormData();
            formData.append('thumbFinal', endFile);
            await videoService.updateThumbnailEnd(videoId, formData);
          }

          if (uploadCount > 0) {
            clearAllTempFiles();
            toast({
              title: uploadCount > 1 ? translateInterfaceText("Thumbnails atualizadas") : translateInterfaceText("Thumbnail atualizada"),
              description: uploadCount > 1
                ? translateInterfaceText("As imagens foram enviadas e salvas com sucesso")
                : translateInterfaceText("A imagem foi enviada e salva com sucesso"),
            });
          } else if (features.pauseThumbnail) {
            toast({
              title: translateInterfaceText("Configurações da thumbnail salvas"),
              description: translateInterfaceText("As configurações da thumbnail de pausa foram salvas com sucesso"),
            });
          }
          break;
        }

        case 'autoplayOverlay':
        case 'autoPlay': {
          // Quando o usuário escolheu o template "Personalizado" e mandou um
          // arquivo de imagem, sobe pro Bunny igual ao fluxo de thumb. Sem
          // arquivo, só fecha o painel — o restante das configs do autoplay é
          // salvo pelo handleSave global (botão Salvar do header).
          const autoplayFile = tempFiles.autoplayCustomImageFile;
          if (autoplayFile) {
            const formData = new FormData();
            formData.append('customImage', autoplayFile);
            const uploadResp = await videoService.updateAutoplayCustomImage(videoId, formData);
            const finalUrl = uploadResp?.customImage;
            if (finalUrl) {
              setFeature('autoplayOverlay', {
                ...features.autoplayOverlay,
                customImage: finalUrl,
                useTemplate: false,
                templateId: 'custom',
              } as any);
            }
            clearAllTempFiles();
            toast({
              title: translateInterfaceText("Imagem do autoplay atualizada"),
              description: translateInterfaceText("A imagem foi enviada e salva com sucesso"),
            });
          }
          break;
        }

        case 'actionButton': {
          // O 422 do backend cai no catch abaixo, que mostra `response.data.message`. O estado
          // não é regravado: os ids gerados pelo front são mantidos pelo backend.
          const { persistActionButtons } = await import('@/lib/action-button/persist');
          await persistActionButtons(videoId, features.actionButtons ?? []);
          toast({
            title: translateInterfaceText("Botões de ação salvos"),
            description: translateInterfaceText("As configurações foram aplicadas com sucesso."),
          });
          break;
        }

        case 'headline': {
          // Apenas fecha o painel sem chamar a API — o save é feito pelo botão "Salvar"
          break;
        }

        case 'responsivePlayer': {
          const responsiveConfig = features.responsivePlayer;
          // Envia null quando desativado para limpar o dado no backend
          const payload = responsiveConfig?.enabled ? responsiveConfig : null;
          await videoService.updateResponsiveConfig(videoId, payload);

          toast({
            title: translateInterfaceText("Exibição por dispositivo atualizada"),
            description: translateInterfaceText("As configurações foram salvas com sucesso"),
          });
          break;
        }

        case 'subtitles': {
          const { subtitleService } = await import('@/services/subtitleService');
          const subtitles = features.subtitles || [];
          const subtitleConfig = features.subtitleConfig;
          const isActive = subtitleConfig?.showByDefault ?? false;

          if (isActive) {
            // Switch ativo → salva configurações de estilo
            if (subtitleConfig) {
              await subtitleService.updateSubtitleConfig(videoId, subtitleConfig);
            }
            toast({
              title: translateInterfaceText("Legendas salvas"),
              description: translateInterfaceText("As configurações de legenda foram aplicadas com sucesso."),
            });
          } else {
            // Switch desativado → remove todas as tracks existentes
            await Promise.all(
              subtitles.map((sub) => subtitleService.deleteSubtitle(videoId, sub.srclang))
            );
            setFeature('subtitles', []);
            toast({
              title: translateInterfaceText("Legendas desativadas"),
              description: translateInterfaceText("Todas as legendas foram removidas do vídeo."),
            });
          }
          break;
        }

        case 'miniHooks': {
          const miniHooks = features.miniHooks;
          if (miniHooks) {
            await videoService.saveMiniHooks(videoId, miniHooks);
            toast({
              title: translateInterfaceText("Mini Hooks salvos"),
              description: translateInterfaceText("As configurações foram aplicadas com sucesso."),
            });
          }
          break;
        }

        case 'conversionJourney': {
          const conversionJourney = features.conversionJourney;
          if (conversionJourney) {
            await videoService.saveConversionJourney(videoId, conversionJourney);
            toast({
              title: translateInterfaceText("Jornada de Conversão salva"),
              description: translateInterfaceText("As configurações foram aplicadas com sucesso."),
            });
          }
          break;
        }

        case 'videoFeedback': {
          const fb = features.videoFeedback;
          if (fb) {
            const { toPlayerConfig } = await import('@/services/videoFeedbackService');
            await videoService.saveVideoFeedback(videoId, {
              haveVideoFeedback: fb.enabled,
              videoFeedbackConfig: fb.enabled ? toPlayerConfig(fb) : null,
            });
            toast({
              title: translateInterfaceText("Feedback do Vídeo salvo"),
              description: translateInterfaceText("As configurações foram aplicadas com sucesso."),
            });
          }
          break;
        }

        case 'salesNotification': {
          const salesNotification = features.salesNotification;
          if (salesNotification) {
            await videoService.saveSalesNotification(videoId, salesNotification);
            toast({
              title: translateInterfaceText("Notificação de Vendas salva"),
              description: translateInterfaceText("As configurações foram aplicadas com sucesso."),
            });
          }
          break;
        }

        case 'finalCountdown': {
          const finalCountdown = features.finalCountdown;
          if (finalCountdown) {
            await videoService.saveFinalCountdown(videoId, finalCountdown);
            toast({
              title: translateInterfaceText("Contagem Final salva"),
              description: translateInterfaceText("As configurações foram aplicadas com sucesso."),
            });
          }
          break;
        }

        default:
          // For other resources, just close the panel (no API call needed yet)
          break;
      }

      // Close the editor panel (exceto quando deve manter aberto, ex: após criar speed test)
      if (!keepEditorOpen) {
        setActiveEditingPanel(null);
        setCurrentFeature(null);
      }
    } catch (error) {
      console.error('Error applying changes:', error);
      const { toast } = await import('@/hooks/use-toast');

      // Extract error message from response if available
      const errorMessage = error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        translateInterfaceText("Ocorreu um erro ao salvar as configurações. Tente novamente.");

      toast({
        title: translateInterfaceText("Erro ao aplicar alterações"),
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, [setActiveEditingPanel, setCurrentFeature, features, user]);

  // Load existing video from library
  const loadExistingVideo = useCallback(async (video: Video) => {
    try {
      // Import videoService dynamically to avoid circular dependencies
      const { videoService } = await import('@/services/videoService');

      // Fetch complete video data from backend including features
      const response = await videoService.getVideoById(video.id);
      const videoData = response?.response || response?.data?.response || response;

      console.log('[loadExistingVideo] Video data from backend:', videoData);

      // Map backend fields to frontend structure
      // Basic video info
      setFeature('videoUrl', videoData.videoDownload || videoData.videoMP4 || video.videoUrl);
      setFeature('videoStreamUrl', videoData.video || '');
      setFeature('videoId', videoData.id_video);
      setFeature('title', videoData.nomeVideo);

      // Controles
      if (videoData.haveControls !== undefined) {
        setFeature('showControls', videoData.haveControls);
      }
      if (videoData.haveProgresso !== undefined) {
        setFeature('showProgressBar', videoData.haveProgresso);
      }
      if (videoData.haveSkip !== undefined) {
        setFeature('skipForward', videoData.haveSkip);
      }
      if (videoData.haveRewind !== undefined) {
        setFeature('rewind', videoData.haveRewind);
      }
      if (videoData.haveVolume !== undefined) {
        setFeature('volumeControl', videoData.haveVolume);
      }
      if (videoData.haveFullScreen !== undefined) {
        setFeature('fullScreen', videoData.haveFullScreen);
      }
      if (videoData.havePlaybackSpeed !== undefined) {
        setFeature('playbackSpeedControl', videoData.havePlaybackSpeed);
      }
      if (videoData.haveStoryboard !== undefined) {
        setFeature('storyboardEnabled', videoData.haveStoryboard);
      }
      if (videoData.haveTvCast !== undefined) {
        setFeature('tvCastEnabled', videoData.haveTvCast);
      }
      if (videoData.storyboardUrl !== undefined) {
        setFeature('storyboardUrl', videoData.storyboardUrl);
      }
      if (videoData.haveTempoAtual !== undefined) {
        setFeature('showCurrentTime', videoData.haveTempoAtual);
      }
      if (videoData.haveBigPlay !== undefined) {
        setFeature('bigPlayButton', videoData.haveBigPlay);
      }
      if (videoData.haveSmallPlay !== undefined) {
        setFeature('smallPlayButton', videoData.haveSmallPlay);
      }
      if (videoData.noneControlsMoreAutoplay !== undefined) {
        setFeature('disableControls', videoData.noneControlsMoreAutoplay);
      }
      if (videoData.controlColor) {
        setFeature('controlsColor', videoData.controlColor);
      }

      // Smart Pause
      if (videoData.haveSmartPause !== undefined) {
        setFeature('smartPause', videoData.haveSmartPause);
      }

      // Bloqueio de pause
      if (videoData.haveBlockPause !== undefined) {
        setFeature('blockPause', videoData.haveBlockPause);
      }

      // Legendas (backend pode retornar como string duplamente serializada)
      const parseJsonField = (value: any) => {
        if (typeof value !== 'string') return value;
        try {
          const parsed = JSON.parse(value);
          return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
        } catch { return value; }
      };
      if (videoData.subtitles) {
        setFeature('subtitles', parseJsonField(videoData.subtitles));
      }
      if (videoData.subtitleConfig) {
        setFeature('subtitleConfig', parseJsonField(videoData.subtitleConfig));
      }

      // Mini Hooks (armazenado como TEXT JSON no MySQL)
      if (videoData.miniHooks) {
        const parsed = parseJsonField(videoData.miniHooks);
        if (parsed && typeof parsed === 'object') {
          setFeature('miniHooks', parsed);
        }
      }

      // Jornada de Conversão (armazenado como JSON no MySQL)
      if (videoData.conversionJourney) {
        const parsed = parseJsonField(videoData.conversionJourney);
        if (parsed && typeof parsed === 'object') {
          setFeature('conversionJourney', parsed);
        }
      }

      // Feedback do Vídeo (haveVideoFeedback + videoFeedbackConfig no backend)
      if (videoData.haveVideoFeedback !== undefined) {
        const parsedCfg = videoData.videoFeedbackConfig
          ? parseJsonField(videoData.videoFeedbackConfig)
          : null;
        import('@/services/videoFeedbackService').then(({ fromPlayerConfig }) => {
          setFeature(
            'videoFeedback',
            fromPlayerConfig(!!videoData.haveVideoFeedback, parsedCfg)
          );
        });
      }

      // Notificação de Vendas e Contagem Final (objetos JSON no Mongo).
      // Hidrata SEMPRE, mesmo quando o vídeo não tem config: o store tem
      // `persist`, então pular a hidratação deixa a config do vídeo anterior no
      // estado e o painel abre ligado num vídeo que nunca teve o recurso.
      const parsedSalesNotification = videoData.salesNotification
        ? parseJsonField(videoData.salesNotification)
        : null;
      setFeature(
        'salesNotification',
        parsedSalesNotification && typeof parsedSalesNotification === 'object'
          ? parsedSalesNotification
          : defaultSalesNotification
      );

      const parsedFinalCountdown = videoData.finalCountdown
        ? parseJsonField(videoData.finalCountdown)
        : null;
      setFeature(
        'finalCountdown',
        parsedFinalCountdown && typeof parsedFinalCountdown === 'object'
          ? parsedFinalCountdown
          : defaultFinalCountdown
      );

      // Recomeçar após o fim
      if (videoData.haveRestart !== undefined) {
        setFeature('haveRestart', videoData.haveRestart);
      }

      // Exibição por Dispositivo
      if (videoData.responsive_config) {
        setFeature('responsivePlayer', videoData.responsive_config);
      }

      // Border
      if (videoData.haveBorder !== undefined) {
        setFeature('border', {
          enabled: videoData.haveBorder,
          color: videoData.borderColor || '#ffffff',
          width: videoData.borderWidth || 2,
          radius: videoData.borderRadius ?? (videoData.haveBorderRadius ? 12 : 0),
          secondaryColor: '#ffffff',
          stylePreset: 'none',
        });
      }

      // Autoplay Overlay
      if (videoData.haveAutoPlay !== undefined) {
        setFeature('autoPlay', videoData.haveAutoPlay);

        // Mapear typesAutoplay ou haveSmallTemplate para templateId
        let templateId = 'autoPlay'; // default
        if (videoData.typesAutoplay) {
          // Mapear valores do backend para frontend
          const typeMap: Record<string, string> = {
            'autoPlay': 'autoPlay',
            'small': 'small',
            'large': 'large',
            'fullScreen': 'fullScreen',
            'transparent': 'transparent',
            'blur': 'blur',
            'animation': 'animation',
            // Sem esta entrada, o load convertia 'custom' em 'autoPlay' e o save
            // seguinte (linha ~96, typesAutoplay) regravava 'autoPlay' no banco —
            // o modo imagem personalizada se perdia ao abrir e salvar o editor.
            'custom': 'custom'
          };
          templateId = typeMap[videoData.typesAutoplay] || 'autoPlay';
        } else if (videoData.haveSmallTemplate) {
          templateId = 'small';
        }

        setFeature('autoplayOverlay', {
          enabled: videoData.haveAutoPlay,
          textTop: videoData.textSuperior || '',
          textBottom: videoData.textInferior || '',
          colors: {
            background: videoData.cor || 'rgba(0, 170, 255, 0.46)',
            text: videoData.corText || '#FFFFFF',
            icon: videoData.autoplayIconColor || videoData.corText || '#FFFFFF',
            border: '#FFFFFF'
          },
          whiteBorder: videoData.haveBorderWhite || false,
          pulseAnimation: videoData.havePulse || false,
          pulseEffect: videoData.havePulse || false,
          customImage: videoData.customImage || undefined,
          useTemplate: !videoData.haveCustomImage,
          showIcon: true,
          templateId: templateId,
          autoRestart: false,
          fontFamily: 'Inter',
          fontSize: '16px',
          fontWeight: '400',
          roundedCorners: true,
          showBorder: videoData.haveBorderWhite || false,
          autoplayType: 'same-video',
          clipStart: 0,
          clipEnd: undefined,
          backgroundStyle: 'darkened',
          backgroundIntensity: 65,
          iconType: 'sound',
          abTestConfig: {
            enabled: false,
            variations: [],
            selectedTemplates: [],
            trafficDistribution: {},
            variantTrafficDistribution: {},
            status: 'draft',
            isRealTimeEnabled: false,
            lastUpdated: ''
          },
          // `null` (vídeo que nunca teve overlay reposicionado) precisa virar
          // NaN, não 0: Number(null) === 0 e Number.isFinite(0) é true, então a
          // hidratação fabricava position {0,0} / width 0. Depois do clamp isso
          // vira x=11%, y=10% no editor — enquanto o player, que testa
          // `typeof === "number"`, mantém o overlay centralizado. Era essa a
          // divergência editor↔player.
          position: (() => {
            const x = toPercentOrNaN(videoData.autoplayPositionXPercent);
            const y = toPercentOrNaN(videoData.autoplayPositionYPercent);
            return Number.isFinite(x) && Number.isFinite(y)
              ? { xPercent: x, yPercent: y }
              : undefined;
          })(),
          size: (() => {
            const w = toPercentOrNaN(videoData.autoplayWidthPercent);
            if (!Number.isFinite(w)) return undefined;
            const h = toPercentOrNaN(videoData.autoplayHeightPercent);
            return {
              widthPercent: w,
              heightMode: 'auto' as const,
              heightPercent: Number.isFinite(h) ? h : undefined,
            };
          })(),
          detectInteraction: videoData.autoplayDetectInteraction === true,
          autoClick: videoData.autoplayAutoClick === true,
          // Tela flutuante (PiP) do autoplay — coluna própria, sem ambiguidade
          // com o PiP dos controles (`havePictureInPicture`).
          havePictureInPicture: videoData.autoplayPictureInPicture === true,
          // Tela cheia do autoplay — a coluna `haveFullScreen` é COMPARTILHADA
          // com o fullscreen dos controles, então um `true` só pode ser atribuído
          // ao autoplay quando os controles estão desligados (com controles
          // ligados o `true` pode ter vindo do botão da barra de controles).
          haveFullScreen: videoData.haveFullScreen === true && !videoData.haveControls,
        });
      }

      // Continue Watching
      if (videoData.haveContinue !== undefined) {
        setFeature('continueWatching', {
          enabled: videoData.haveContinue,
          threshold: 5,
          expires: 7,
          message: videoData.textMessage || '',
          buttonText: videoData.textContinue || '',
          restartText: videoData.textRestart || ''
        });

        // Mapear typesContinue para template
        const continueTemplate = videoData.typesContinue === '' ? 'continue' : (videoData.typesContinue || 'template1');

        setFeature('continueWatchingOverlay', {
          backgroundColor: videoData.backgroundContinue || '#0E0E0E',
          textColor: videoData.continueTextColor || '#FFFFFF',
          playButtonBgColor: videoData.continuePlayButtonBgColor || '',
          restartButtonBgColor: videoData.continueRestartButtonBgColor || '',
          buttonColor: '#1D4FFF',
          buttonTextColor: '#FFFFFF',
          blurBackground: true,
          showTime: true,
          position: 'center',
          enabled: videoData.haveContinue,
          title: "Retomar reprodução",
          message: videoData.textMessage || '',
          continueText: videoData.textContinue || '',
          restartText: videoData.textRestart || '',
          overlayOpacity: 75,
          animationEntrance: 'fade',
          size: 'small',
          template: continueTemplate,
          useTemplate: true,
          autoResume: {
            enabled: false,
            delay: 5,
            showCountdown: true
          },
          soundAlert: {
            enabled: false,
            type: 'click'
          }
        });
      }

      // Audience (Ao Vivo)
      if (videoData.haveAoVivo !== undefined) {
        setFeature('audience', {
          enabled: videoData.haveAoVivo,
          viewersCount: videoData.simulationAoVivo ? parseInt(videoData.simulationAoVivo) || undefined : undefined,
          speed: (videoData.audienceSimulationSpeed as any) || 'medium',
          message: videoData.textAoVivo || '',
          style: {
            textColor: videoData.fontColorAoVivo || '#ffffff',
            backgroundColor: videoData.backgroundAoVivo || '#000000',
            useTransparency: true,
            opacity: 70
          }
        });
      }

      // Headline Overlay - usando estrutura HeadlineOverlay completa
      if (videoData.haveHeadline !== undefined) {
        // Normaliza tamanho vindo do backend (número, string numérica ou enum
        // legado) para number em px. Tolerante a dados antigos não migrados.
        const LEGACY_SIZE_MAP: Record<string, number> = {
          small: 16, medium: 24, large: 32, xlarge: 40,
          pequeno: 16, medio: 24, grande: 32,
        };
        const resolveSize = (raw: any, defaultPx: number): number => {
          if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return raw;
          if (typeof raw === 'string') {
            const num = parseInt(raw, 10);
            if (!isNaN(num) && num > 0) return num;
            const legacy = LEGACY_SIZE_MAP[raw.trim().toLowerCase()];
            if (legacy) return legacy;
          }
          return defaultPx;
        };

        setFeature('headline', {
          enabled: videoData.haveHeadline,
          name: '',
          headline: {
            text: videoData.headlineText || '',
            size: resolveSize(videoData.headlineSize, 24),
            fontFamily: (videoData.headlineTypeFont as 'Inter' | 'Arial' | 'sans-serif') || 'Inter',
            fontWeight: videoData.headlineFontWeight === 'bold' ? 'bold' : 'normal',
            color: videoData.headlineColor || '#ffffff'
          },
          subheadline: {
            text: videoData.subHeadlineText || '',
            size: resolveSize(videoData.subHeadlineSize, 16),
            fontFamily: (videoData.headlineTypeFont as 'Inter' | 'Arial' | 'sans-serif') || 'Inter',
            fontWeight: 'normal',
            color: videoData.subHeadlineColor || '#e5e5e5'
          },
          textMode: { enabled: !videoData.isHTMLSelected },
          htmlMode: { enabled: videoData.isHTMLSelected || false, code: videoData.headlineHtmlCode || '' },
          imageMode: { enabled: false },
          timing: {
            start: videoData.headlineInitialDelay || '',
            end: videoData.headlineEndDelay || '',
            showThroughout: videoData.headlineShowThroughout ?? true,
          },
          position: 'bottom-center',
          offsetX: 0,
          offsetY: 0,
          background: {
            enabled: true,
            type: 'solid',
            color: 'rgba(0, 0, 0, 0.7)',
            opacity: 0.7,
            borderRadius: 8,
            padding: 16
          },
          animation: {
            enabled: true,
            type: 'fade',
            duration: 0.5,
            delay: 0
          },
          abTestConfig: {
            enabled: false,
            variations: [],
            status: 'draft',
            trafficSplit: 50
          }
        });
      }

      // Logo - usando estrutura VideoLogo completa
      // logoImg = URL da imagem, urlLogo = link de destino ao clicar
      if (videoData.logoImg) {
        // Mapear posição do backend para frontend
        const logoPositionMap: Record<string, 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'> = {
          'S_Esquerda': 'top-left',
          'S_Meio': 'top-center',
          'S_Direita': 'top-right',
          'I_Esquerda': 'bottom-left',
          'I_Meio': 'bottom-center',
          'I_Direita': 'bottom-right',
        };

        setFeature('logo', {
          enabled: true,
          url: videoData.logoImg,
          imageUrl: videoData.logoImg,
          linkUrl: videoData.urlLogo || '',
          position: logoPositionMap[videoData.selectedLogoPosition] || 'top-left',
          size: videoData.logoSize || 10,
          opacity: parseInt(videoData.transparencyLogo) || 100
        });
      }

      // Botões de ação (lista)
      setFeature('actionButtons', actionButtonsFromVideo(videoData));

      // Thumbnails - usando estrutura ThumbnailOverlay completa
      if (videoData.thumb) {
        // Mapear posição do botão de thumb
        const thumbPositionMap: Record<string, string> = {
          'S_Esquerda': 'top-left',
          'S_Meio': 'top-center',
          'S_Direita': 'top-right',
          'M_Esquerda': 'center-left',
          'M_Direita': 'center-right',
          'I_Esquerda': 'bottom-left',
          'I_Meio': 'bottom-center',
          'I_Direita': 'bottom-right',
        };

        setFeature('pauseThumbnail', {
          enabled: true,
          imageUrl: videoData.thumb,
          pauseMode: (videoData.thumbPauseMode as 'always' | 'timeRange') || 'always',
          timeRange: {
            start: videoData.thumbPauseTimeStart || '',
          },
          // Botão de ação na thumbnail de pause
          actionButton: videoData.haveButtonThumb ? {
            text: videoData.textButton || '',
            link: videoData.textLink || '',
            size: (videoData.selectedSize as 'small' | 'medium' | 'large') || 'medium',
            position: (thumbPositionMap[videoData.selectedPosition] || 'center') as any,
            colors: {
              background: videoData.backgroundButton || '#3b82f6',
              text: videoData.textButtonColor || '#ffffff',
              hover: videoData.backgroundButtonHover || '#2563eb',
              hoverText: videoData.textButtonColorHover || '#ffffff'
            }
          } : undefined
        });
      }

      if (videoData.thumbInicio) {
        setFeature('initialThumbnail', {
          enabled: true,
          imageUrl: videoData.thumbInicio
        });
      }

      if (videoData.thumbFinal) {
        setFeature('endThumbnail', {
          enabled: true,
          imageUrl: videoData.thumbFinal
        });
      }

      // Playback Speed (Turbo)
      if (videoData.haveTurbo) {
        setFeature('playbackSpeed', videoData.turboVelocity || 1);
      }

      // Progress Bar / Fake Bar
      if (videoData.corBar) {
        setFeature('progressBarColor', videoData.corBar);
      }
      if (videoData.haveFakeBar !== undefined) {
        const fakeBarHeight = videoData.fakeBarHeight || 12;
        setFeature('progressBar', {
          enabled: videoData.haveFakeBar,
          color: videoData.corBar || '#3B82F6',
          height: fakeBarHeight,
          showOnHover: false,
          animated: false
        });
        setFeature('progressBarHeight', fakeBarHeight);
      }

      // Reset state
      setIsSaved(false);
      setHasUnsavedChanges(false);

      toast({
        title: translateInterfaceText("Vídeo carregado"),
        description: translateInterfaceText("\"{value0}\" está pronto para personalização", { value0: (videoData.nomeVideo) })
      });
    } catch (error) {
      console.error('Error loading video from backend:', error);

      // Fallback to basic video data if backend fails
      setFeature('videoUrl', video.videoUrl);
      setFeature('videoId', video.id);
      setFeature('title', video.title);

      toast({
        title: translateInterfaceText("Vídeo carregado"),
        description: translateInterfaceText("\"{value0}\" está pronto para personalização (configurações padrão)", { value0: (video.title) }),
        variant: "default"
      });
    }
  }, [setFeature, setIsSaved, setHasUnsavedChanges, toast]);

  // Reset editor state (useful when entering personalization page fresh)
  const resetEditorState = useCallback(() => {
    setHasUnsavedChanges(false);
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  }, [setHasUnsavedChanges]);

  // Clean up navigation event listener
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return {
    features,
    setFeature,
    currentFeature,
    setCurrentFeature,
    modalTitle,
    modalSubtitle,
    isUploading,
    setIsUploading,
    uploadProgress,
    setUploadProgress,
    showUnsavedDialog,
    setShowUnsavedDialog,
    showEmbedModal,
    setShowEmbedModal,
    isSaved,
    setIsSaved,
    handleSave,
    handleCancel,
    handleNavigate,
    confirmNavigation,
    handleFeatureClick,
    pendingNavigation,
    setPendingNavigation,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    activeEditingPanel,
    setActiveEditingPanel,
    handleCloseEditor,
    handleApplyChanges,
    loadExistingVideo,
    resetEditorState,
  };
}
